import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

const RARITY_COLORS = {
  bronze: '#CD7F32',
  silver: '#C0C0C0',
  gold: '#FFD700',
  platinum: '#E5E4E2'
};

const RARITY_GRADIENTS = {
  bronze: 'from-amber-700 to-amber-500',
  silver: 'from-gray-400 to-gray-300',
  gold: 'from-yellow-500 to-yellow-300',
  platinum: 'from-purple-300 to-blue-200'
};

const BadgeEarnedPopup = () => {
  const dispatch = useDispatch();
  const awardedBadges = useSelector(state => state.auth.awardedBadges) || [];
  const [currentBadge, setCurrentBadge] = useState(null);
  const [queue, setQueue] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (awardedBadges.length > 0) {
      setQueue(prev => [...prev, ...awardedBadges]);
      // Clear from Redux
      dispatch({ type: 'CLEAR_AWARDED_BADGES' });
    }
  }, [awardedBadges, dispatch]);

  useEffect(() => {
    if (!currentBadge && queue.length > 0) {
      setCurrentBadge(queue[0]);
      setQueue(prev => prev.slice(1));
      setIsAnimating(true);
    }
  }, [currentBadge, queue]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => setCurrentBadge(null), 300);
  };

  if (!currentBadge) return null;

  const rarityColor = RARITY_COLORS[currentBadge.rarity] || RARITY_COLORS.bronze;
  const gradient = RARITY_GRADIENTS[currentBadge.rarity] || RARITY_GRADIENTS.bronze;

  return (
    <div
      className={`fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 transition-opacity duration-300 ${
        isAnimating ? 'opacity-100' : 'opacity-0'
      }`}
      onClick={handleClose}
    >
      <div
        className={`bg-white rounded-2xl shadow-2xl max-w-sm w-full overflow-hidden transform transition-all duration-500 ${
          isAnimating ? 'scale-100 translate-y-0' : 'scale-75 translate-y-8'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${gradient} py-6 text-center`}>
          <p className="text-white/80 text-sm font-medium uppercase tracking-wider mb-2">Badge Earned!</p>
          <div className="relative inline-block">
            {currentBadge.image ? (
              <img
                src={currentBadge.image}
                alt={currentBadge.title}
                className="w-28 h-28 rounded-full object-cover mx-auto border-4 shadow-lg"
                style={{ borderColor: 'white' }}
              />
            ) : (
              <div
                className="w-28 h-28 rounded-full bg-white/30 flex items-center justify-center mx-auto border-4 shadow-lg"
                style={{ borderColor: 'white' }}
              >
                <span className="text-5xl">🏆</span>
              </div>
            )}
            {/* Sparkle effect */}
            <div className="absolute -top-1 -right-1 text-2xl animate-bounce">✨</div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 text-center">
          <h3 className="text-xl font-bold text-[#054581] mb-1">{currentBadge.title}</h3>
          <span
            className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase mb-3"
            style={{ backgroundColor: rarityColor, color: '#333' }}
          >
            {currentBadge.rarity}
          </span>
          {currentBadge.description && (
            <p className="text-gray-500 text-sm mb-4">{currentBadge.description}</p>
          )}
          <button
            onClick={handleClose}
            className="w-full py-2.5 bg-[#1086a4] hover:bg-[#054581] text-white rounded-lg font-semibold transition-colors"
          >
            {queue.length > 0 ? 'Next Badge' : 'Awesome!'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BadgeEarnedPopup;
